import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useStores } from "@/hooks/use-stores";
import { supabase } from "@/lib/supabase";
import { Copy, ExternalLink, Plus, RefreshCw } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";

export function StoresPage() {
	const { stores, loading, refetch } = useStores();
	const [showForm, setShowForm] = useState(false);

	if (loading) {
		return (
			<div className="flex items-center justify-center py-20 text-muted-foreground">
				Loading stores...
			</div>
		);
	}

	return (
		<div className="space-y-8">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold tracking-tight">Stores</h1>
					<p className="text-muted-foreground">Manage your connected Shopify stores</p>
				</div>
				<Button onClick={() => setShowForm(!showForm)}>
					<Plus className="mr-2 h-4 w-4" />
					Add Store
				</Button>
			</div>

			{showForm && (
				<AddStoreForm
					onSuccess={() => {
						setShowForm(false);
						refetch();
					}}
					onCancel={() => setShowForm(false)}
				/>
			)}

			{stores.length === 0 && !showForm ? (
				<Card>
					<CardContent className="flex flex-col items-center justify-center py-12">
						<p className="text-lg font-medium text-muted-foreground">No stores connected</p>
						<p className="text-sm text-muted-foreground">
							Add your first Shopify store to start converting emails into orders.
						</p>
						<Button className="mt-4" onClick={() => setShowForm(true)}>
							<Plus className="mr-2 h-4 w-4" />
							Add Your First Store
						</Button>
					</CardContent>
				</Card>
			) : (
				<div className="grid gap-4 md:grid-cols-2">
					{stores.map((store) => (
						<Card key={store.id}>
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle className="text-lg">{store.name}</CardTitle>
									<Badge
										variant={
											store.status === "active"
												? "success"
												: store.status === "paused"
													? "warning"
													: "destructive"
										}
									>
										{store.status}
									</Badge>
								</div>
								<CardDescription>
									<a
										href={`https://${store.shopify_domain}`}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center gap-1 hover:underline"
									>
										{store.shopify_domain}
										<ExternalLink className="h-3 w-3" />
									</a>
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-3">
								<div>
									<p className="text-xs font-medium text-muted-foreground">Order Email Address</p>
									<div className="flex items-center gap-2">
										<code className="text-sm">{store.generated_email}</code>
										<Button
											variant="ghost"
											size="icon"
											className="h-6 w-6"
											onClick={() => navigator.clipboard.writeText(store.generated_email)}
										>
											<Copy className="h-3 w-3" />
										</Button>
									</div>
									{store.custom_email && (
										<p className="text-xs text-muted-foreground mt-1">
											Custom: {store.custom_email}
										</p>
									)}
								</div>
								<div className="flex gap-4 text-sm">
									<span>
										<strong>{store.products_synced_count}</strong> products synced
									</span>
								</div>
							</CardContent>
							<CardFooter className="gap-2">
								<Button variant="outline" size="sm" asChild>
									<Link to={`/stores/${store.id}`}>Manage</Link>
								</Button>
								<SyncButton storeId={store.id} onDone={refetch} />
							</CardFooter>
						</Card>
					))}
				</div>
			)}
		</div>
	);
}

// ── Add Store Form ───────────────────────────────────────────────────────────

function AddStoreForm({
	onSuccess: _onSuccess,
	onCancel,
}: {
	onSuccess: () => void;
	onCancel: () => void;
}) {
	const [name, setName] = useState("");
	const [domain, setDomain] = useState("");
	const [customEmail, setCustomEmail] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		setError(null);
		setSaving(true);

		try {
			const {
				data: { user },
			} = await supabase.auth.getUser();

			if (!user) throw new Error("Not authenticated");

			// Initiate OAuth flow via server function
			const res = await fetch("/api/shopify-auth", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					shopify_domain: domain,
					name,
					custom_email: customEmail || null,
					user_id: user.id,
				}),
			});

			if (!res.ok) {
				throw new Error(await getErrorMessage(res, "Failed to initiate Shopify connection"));
			}

			const { data } = await res.json();

			// Redirect to Shopify OAuth consent screen
			window.location.href = data.oauth_url;
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to connect store");
			setSaving(false);
		}
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>Connect a Shopify Store</CardTitle>
				<CardDescription>
					Enter your store details below. You&apos;ll be redirected to Shopify to authorize the
					connection.
				</CardDescription>
			</CardHeader>
			<form onSubmit={handleSubmit}>
				<CardContent className="space-y-4">
					{error && (
						<div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
					)}
					<div className="space-y-2">
						<Label htmlFor="store-name">Store Name</Label>
						<Input
							id="store-name"
							placeholder="My Awesome Store"
							value={name}
							onChange={(e) => setName(e.target.value)}
							required
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="shopify-domain">Shopify Domain</Label>
						<Input
							id="shopify-domain"
							placeholder="my-store.myshopify.com"
							value={domain}
							onChange={(e) => setDomain(e.target.value)}
							required
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="custom-email">
							Custom Email Address{" "}
							<span className="font-normal text-muted-foreground">(optional)</span>
						</Label>
						<Input
							id="custom-email"
							type="email"
							placeholder="orders@yourdomain.com"
							value={customEmail}
							onChange={(e) => setCustomEmail(e.target.value)}
						/>
						<p className="text-xs text-muted-foreground">
							If set, configure this as a send-from alias in the connected Zoho mailbox so
							customers see it on replies. Otherwise, replies will use the generated address.
						</p>
					</div>
				</CardContent>
				<CardFooter className="gap-2">
					<Button type="submit" disabled={saving}>
						{saving ? "Redirecting to Shopify..." : "Install on Shopify"}
					</Button>
					<Button type="button" variant="outline" onClick={onCancel}>
						Cancel
					</Button>
				</CardFooter>
			</form>
		</Card>
	);
}

// ── Sync Button ──────────────────────────────────────────────────────────────

function SyncButton({ storeId, onDone }: { storeId: string; onDone: () => void }) {
	const [syncing, setSyncing] = useState(false);

	async function handleSync() {
		setSyncing(true);
		try {
			await fetch("/api/sync-products", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ store_id: storeId }),
			});
			onDone();
		} finally {
			setSyncing(false);
		}
	}

	return (
		<Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}>
			<RefreshCw className={`mr-2 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
			{syncing ? "Syncing..." : "Sync Products"}
		</Button>
	);
}

async function getErrorMessage(res: Response, fallback: string) {
	const contentType = res.headers.get("Content-Type") ?? "";

	if (contentType.includes("application/json")) {
		const body = await res.json();
		return body.error?.message ?? fallback;
	}

	const text = await res.text();
	return text || fallback;
}
